<script setup lang="ts">
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { endManagingSession } from '@/api/impersonation'

// Shown on every page while an admin is running a "manage as user" session. This has to be loud and
// always visible: in that state the whole SPA — nav, account, cart, permissions — is the OTHER
// user's, and an admin who forgets they are impersonating can take real actions as that person.
const auth = useAuthStore()

const impersonating = computed(() => auth.user?.impersonating === true)
const who = computed(() => {
  const u = auth.user
  if (!u) return 'another user'
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
  return name || u.username || `user #${u.userId}`
})
</script>

<template>
  <div v-if="impersonating" class="imp-strip" role="alert" data-test="impersonation-banner">
    <span class="imp-text">
      Managing as <strong>{{ who }}</strong> — actions you take here are recorded as theirs.
    </span>
    <button class="imp-end" type="button" data-test="end-managing" @click="endManagingSession()">
      End managing session
    </button>
  </div>
</template>

<style scoped>
.imp-strip {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px 18px;
  padding: 10px 22px;
  /* Deliberately not the admin strip's palette — this is a mode the admin needs to notice, so it
     reads as a warning rather than as more chrome. */
  background: #8a2b06;
  color: #fff;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 600;
  text-align: center;
}

.imp-end {
  background: #fff;
  color: #8a2b06;
  border: none;
  border-radius: 3px;
  padding: 6px 12px;
  font: inherit;
  cursor: pointer;
}

.imp-end:hover {
  background: #ffe9df;
}
</style>
